import React, { ChangeEvent, useEffect, useMemo, useState, useCallback } from "react";
import { DialogHeader, DialogBody, DialogFooter } from "components/common/dialogs/GenericDialog";
import {
  WarningCircle,
  X,
  HouseLine,
  Briefcase,
  MapPin,
  Users,
  Info,
  Globe,
  PencilSimpleLine,
  ShieldCheck,
  MapPinArea,
  Spinner,
  Warning,
  CheckCircle,
} from "@phosphor-icons/react";
import { AddressFields, AddressPayloadType, ErrorType } from "lib/types/address";
import { postRequest } from "lib/core/apiClient";
import { useUserContext } from "lib/contexts/UserProvider";
import { useLocale } from "lib/hooks/useLocale";
import { putRequest, getRequest } from "lib/core/apiClient";
import { BaseInputField } from "@shopflo/ui/base-input-field";
import {
  getDetailsByZip,
  getAddressConfigByCountryCode,
  getProvincesByCountryCode,
} from "lib/core/apiMethods";
import {
  addDefaultCountryCode,
  capitalizeFirstCharacter,
  toTitleCase,
  inIframe,
  isEmptyObj,
  redirectUrl,
  classNames,
  getPhoneInputMaxLength,
} from "lib/utils/helpers";
import PrimaryButton from "components/common/buttons/PrimaryButton";
import { errorToast } from "lib/utils/toasters";
import { setUserOrderHistory } from "lib/utils/user";
import { handleOutOfStockResponse } from "lib/utils/checkout";
import { OTPVerificationDetails, UserType } from "lib/types/user";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";
import { AddressSource, analyticsEvents, analyticsTypes, apiURI, IMAGE_CDN_URL } from "lib/utils/constants";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import PhoneInput, { Country, Value as PhoneInputValueType } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import DropDown from "components/common/fields/DropDown";
import FieldWrapper from "./FieldWrapper";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { BaseTextArea } from "@shopflo/ui/base-text-area";
import { Autocomplete, type AutocompleteOption } from "@shopflo/ui/autocomplete";
import { ShippingHandlesType, CheckoutActions } from "lib/types/checkout";
import useForm, { FormField, ValidationSchema } from "lib/hooks/useForm";
import { useDebounceCallback } from "lib/hooks/useDebounceCallback";
import { getAddressFields, filterDisabledAddressFields } from "lib/utils/address";
import GenericBannerStrip from "components/common/notification-strips/GenericBannerStrip";
import { sendOtpWithPhoneNumber, setTwoStepAuthToken } from "lib/utils/auth";
import { constants } from "lib/utils/constants";
import { useSearchParams } from "react-router-dom";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { mutate } from "swr";
import { getUpsellUrl } from "lib/utils/upsell";
import { getETDText } from "lib/utils/merchant";
import { useAddressETD } from "lib/hooks/useAddressETD";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import { Icon, PhosphorIcon } from "@shopflo/ui/icon";

interface AddressFormProps {
  addressId: string;
  initialFormErrors?: Record<string, ErrorType | undefined>;
}
enum ADDRESS_TYPES {
  HOME = "HOME",
  WORK = "WORK",
  OTHER = "OTHER",
  FRIENDS_AND_FAMILY = "FRIENDS/FAMILY",
}

type AddressTypeValue = typeof ADDRESS_TYPES[keyof typeof ADDRESS_TYPES];

interface AddressTypeProps {
  value?: AddressTypeValue;
  id: string;
  label: string;
  isDisabled?: boolean;
  icon: PhosphorIcon;
}

import { ADDRESS_ERROR_CODE_MAP, VALID_ADDRESS_FIELDS } from "lib/utils/constants";
import { extractApiError } from "lib/utils/error";
import { usePopupOrchestrator } from "lib/contexts/PopupOrchestratorProvider";

const AddressForm: React.FC<AddressFormProps> = ({ addressId, initialFormErrors }) => {
  const [searchParams] = useSearchParams();
  const { t } = useLocale();
  const {
    state: { user },
    actions: { setUserAddress },
  } = useUserContext();
  const {
    state: {
      checkoutId,
      isTwoStepCheckout,
      checkoutToken,
      shopifySessionId,
      originUrl,
      actionUrls,
      checkoutValidations,
      uid,
    },
    actions: {
      setShippingHandles,
      setCheckoutView,
      updateCheckoutBasedOnCheckoutResponse,
      setCheckoutModal,
      setOOSItems,
      setCheckoutValidations,
      setEtdText,
    },
  } = useCheckoutContext();
  const { closePopup, openPopup } = usePopupOrchestrator();
  const {
    state: { merchant, checkoutMetadata },
  } = useMerchantContext();
  const {
    actions: { mutatePayment },
  } = usePaymentContext();
  const { isETDEnabled } = useAddressETD(addressId);
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const [isNewAddress, setIsNewAddress] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [stateDisabled, setStateDisabled] = useState<boolean>(false);
  const [editAddressKeys, setEditAddressKeys] = useState<string[]>([]);
  const [showOthersInput, setShowOthersInput] = useState<boolean>(false);
  const [phoneInputFocused, setPhoneInputFocused] = useState<boolean>(false);
  const [countries, setCountries] = useState<any>([]);
  const [states, setStates] = useState<any>([]);
  const [fieldsConfig, setFieldsConfig] = useState<any[]>([]);
  // const [alternateNumberEnabled, setAlternateNumberEnabled] = useState<boolean>(false);
  const [isLocationFieldsExpanded, setIsLocationFieldsExpanded] = useState<boolean>(false);

  const validationSchema: ValidationSchema = useMemo(() => {
    if (!merchant) return {} as ValidationSchema;
    const schema = getAddressFields(fieldsConfig, states, editAddressKeys);
    return schema;
  }, [fieldsConfig, editAddressKeys, merchant, states]);
  const { initialCheckoutStep } = checkoutMetadata ?? {};

  const initialState: AddressFields = isTwoStepCheckout
    ? {
        zip: user?.default_shipping_address?.zip ?? "",
        address1: user?.default_shipping_address?.address1 ?? "",
        address2: user?.default_shipping_address?.address2 ?? "",
        name: capitalizeFirstCharacter(user?.default_shipping_address?.name ?? ""),
        phone: user?.default_shipping_address?.phone ?? "",
        city: user?.default_shipping_address?.city ?? "",
        state: user?.default_shipping_address?.state ?? "",
        state_code: user?.default_shipping_address?.state_code ?? "",
        country: user?.default_shipping_address?.country || "India",
        country_code: user?.default_shipping_address?.country_code || "IN",
        type: "Home",
        email: user?.default_shipping_address?.email?.trim() ?? "",
        alternate_number: user?.default_shipping_address?.alternate_number ?? "",
      }
    : {
        zip: "",
        address1: "",
        address2: "",
        name: capitalizeFirstCharacter(user?.name ?? ""),
        phone: user?.phone ?? "",
        city: "",
        state: "",
        state_code: "",
        country: "India",
        country_code: "IN",
        type: "Home",
        email: user?.email?.trim() ?? "",
        alternate_number: "",
      };

  const {
    handleSubmit,
    inputProps,
    setValues,
    setErrors,
    setValueOf,
    handleFieldChange,
    state: { values, errors, validations },
  } = useForm({ initialState, validationSchema, initialErrorState: initialFormErrors });

  const hasLocationErrors = Boolean(
    errors?.city?.status ||
      errors?.state?.status ||
      errors?.state_code?.status ||
      errors?.country?.status ||
      errors?.country_code?.status,
  );

  const [addressAutocompleteOptions, setAddressAutocompleteOptions] = useState<AutocompleteOption[]>([]);
  const [isLoadingAddressSuggestions, setIsLoadingAddressSuggestions] = useState(false);

  const addressTypes: AddressTypeProps[] = [
    {
      value: ADDRESS_TYPES.HOME,
      id: "home_opt",
      label: t("home"),
      isDisabled: Boolean(user?.hasHomeAddr),
      icon: HouseLine,
    },
    {
      value: ADDRESS_TYPES.FRIENDS_AND_FAMILY,
      id: "friends_and_family_opt",
      label: t("friends_slash_family"),
      icon: Users,
    },
    {
      value: ADDRESS_TYPES.WORK,
      id: "work_opt",
      label: t("work"),
      isDisabled: Boolean(user?.hasWorkAddr),
      icon: Briefcase,
    },
    {
      value: ADDRESS_TYPES.OTHER,
      id: "other_opt",
      label: t("other"),
      isDisabled: false,
      icon: MapPin,
    },
  ];

  useEffect(() => {
    sendAnalyticsEvent({
      eventName:
        !Boolean(addressId) || Boolean(addressId === "ADDRESS_NEW")
          ? analyticsEvents.FLO_ADDRESS_NEW_FORM_LOADED
          : analyticsEvents.FLO_ADDRESS_EDIT_FORM_LOADED,
      eventType: "load",
    });
    if (isNewAddress && !isTwoStepCheckout) {
      handleInternationalAddresses();
      handleCountrySelection("IN");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!Boolean(addressId) || Boolean(addressId === "ADDRESS_NEW")) {
      setIsNewAddress(true);
      return;
    }
    setIsNewAddress(false);
    getAddressById(addressId);
  }, [addressId]);

  const isAddressBannerEnabled = useCallback(() => {
    const filteredData = checkoutMetadata?.uiAttributes?.bannerConfig?.filter(
      (config: any) => config.type === "ORDER_SUMMARY" && config?.enabled,
    );
    return Boolean(filteredData?.length);
  }, [checkoutMetadata?.uiAttributes?.bannerConfig]);

  const getAddressById = async (addressId: string) => {
    if (!Boolean(addressId)) return;

    try {
      setIsLoading(true);
      const response: any = await getRequest(`/attributes/v1/address/${addressId}`);
      const promises = [
        handleCountrySelection(response?.data?.country_code ?? "IN", Boolean(initialFormErrors)),
      ];

      if (!Boolean(response?.data?.country_code) && response?.data?.country?.toUpperCase() === "INDIA") {
        response.data.country_code = "IN";
      }
      if (response?.data?.country_code !== "IN") {
        promises.push(handleInternationalAddresses());
      }
      if (
        response?.data?.country_code === "IN" &&
        !Boolean(response?.data?.phone?.startsWith("+")) &&
        Boolean(response?.data?.phone)
      ) {
        response.data.phone = `+91${response?.data?.phone}`;
      }
      await Promise.all(promises);
      const addressKeys = Object.keys(response.data).filter((key) => Boolean(response.data[key]));
      setEditAddressKeys(addressKeys);
      let phoneNumber = Boolean(response?.data?.phone) ? response?.data?.phone : user?.phone;
      phoneNumber = phoneNumber?.replaceAll(" ", "");
      setValues({
        zip: response?.data?.zip,
        address1: response?.data?.address1,
        address2: response?.data?.address2,
        name: capitalizeFirstCharacter(response?.data?.name ?? ""),
        phone: phoneNumber,
        city: response?.data?.city,
        state: toTitleCase(response?.data?.state ?? ""),
        state_code: response?.data?.state_code,
        country: response?.data?.country,
        country_code: response?.data?.country_code,
        type: response?.address_type?.trim() || "Other",
        email: Boolean(response?.data?.email) ? response?.data?.email?.trim() : user?.email?.trim(),
        alternate_number: response?.data?.alternate_number?.replaceAll(" ", ""),
      });
      setIsLoading(false);
      if (!Object.values(ADDRESS_TYPES).includes(response?.address_type?.toUpperCase() as AddressTypeValue)) {
        setShowOthersInput(true);
      }
    } catch (err) {
      setIsLoading(false);
      console.error(err);
    }
  };

  const handleZipChange = async (zip: string) => {
    const countryCode = values?.country_code;
    if (countryCode === "IN" && Boolean(/\D/.test(zip))) return; //Checks for special characters
    handleFieldChange(FormField.zip, zip);
    let zipDetails: any = {};

    try {
      if (Boolean(zip) && countryCode === "IN" && zip?.length === 6) {
        zipDetails = await getDetailsByZip(zip);
        setValues((oldValues: any) => ({
          ...oldValues,
          city: capitalizeFirstCharacter(zipDetails.district),
          state: toTitleCase(zipDetails.state),
          state_code: zipDetails.state_code,
          country: capitalizeFirstCharacter(zipDetails.country),
        }));
        setStateDisabled(true);
      }

      setErrors((oldValues) => ({
        ...oldValues,
        city: { ...oldValues.city, status: false },
        state: { ...oldValues.state, status: false },
        country: { ...oldValues.country, status: false },
        state_code: { ...oldValues.state_code, status: false },
        zipValidation: { ...oldValues.zipValidation, status: false },
      }));
    } catch (e) {
      if (merchant?.addressConfig?.disable_unavailable_pincode) {
        return;
      }

      if (isEmptyObj(zipDetails)) {
        setStateDisabled(false);
        setValues((oldValues) => ({
          ...oldValues,
          state: "",
          state_code: "",
        }));
        setErrors((oldValues) => ({
          ...oldValues,
          zipValidation: { ...oldValues.zipValidation, status: true },
        }));
        return;
      }
    }
  };

  const handleAddressFieldError = (error: unknown): boolean => {
    if (typeof error !== "object" || error === null) return false;
    const errorObj = error as Record<string, unknown>;
    const code = typeof errorObj.code === "string" ? errorObj.code : undefined;
    const data =
      typeof errorObj.data === "object" && errorObj.data !== null
        ? (errorObj.data as Record<string, unknown>)
        : undefined;
    const field = typeof data?.field === "string" ? data.field : undefined;
    const messageKey = code ? ADDRESS_ERROR_CODE_MAP[code] : undefined;
    if (messageKey && field && VALID_ADDRESS_FIELDS.has(field)) {
      setErrors((oldValues) => ({
        ...oldValues,
        [field]: { status: true, message: t(messageKey) },
      }));
      setIsLoading(false);
      return true;
    }
    return false;
  };

  const onDialogFormSubmit = async () => {
    const addressData = filterDisabledAddressFields(
      {
        ...values,
        country: values?.country || "India",
        country_code: values?.country_code || "IN",
        address1: values.address1?.replace(/<[^>]*>?/gm, "")?.trim(),
        address2: values.address2?.replace(/<[^>]*>?/gm, "")?.trim(),
        name: values.name.trim(),
        city: values.city.trim(),
        email: values.email?.trim(),
        zip: values.zip?.trim(),
      },
      fieldsConfig,
    );
    const payload: AddressPayloadType = {
      source: AddressSource.SHIPPING_ADDRESS,
      address_type: values.type?.trim() || "OTHER",
      address_data: addressData,
    };
    try {
      setIsLoading(true);
      let response;
      if (isNewAddress) {
        response = await postRequest(`/checkout/${checkoutId}/address`, payload, "KRATOS_PRIVATE");
      } else if (Boolean(addressId)) {
        response = await putRequest(
          `/checkout/${checkoutId}/address/${addressId}`,
          payload,
          "KRATOS_PRIVATE",
        );
      } else {
        setCheckoutView("ADDRESS_LIST");
        setIsLoading(false);
      }

      const newAddressId = response?.addresses?.shipping_address_id;
      if (Boolean(isETDEnabled) && Boolean(newAddressId)) {
        try {
          const etdResponse = await getRequest(
            `/checkout/${uid}/address/${newAddressId}/etd`,
            "KRATOS_PRIVATE",
          );
          setEtdText({ addressId: newAddressId, text: getETDText(etdResponse) });
        } catch (e) {
          console.error("Etd fetch failed");
        }
      }
      const accountResponse = await getRequest("/gatekeeper/accounts?include=address", "KRATOS_PRIVATE");
      handleResponseSuccess(response, accountResponse);
      setIsLoading(false);

      if (response?.metadata?.error_response?.code === "OUT_OF_STOCK") {
        handleOutOfStockResponse({
          items: response?.metadata?.error_response?.metadata?.items,
          addons: response?.metadata?.error_response?.metadata?.addons,
          setOOSItems,
          openPopup,
          sendAnalyticsEvent,
        });
        return;
      }

      const showShippingHandles = response?.metadata?.show_shipping_handle_selector ?? false;
      const shippingHandles = response?.metadata?.available_shipping_handles ?? [];
      setShippingHandles(shippingHandles as ShippingHandlesType);
      if (shippingHandles.length > 0 && Boolean(showShippingHandles) && initialCheckoutStep !== "PAYMENTS") {
        openPopup("SHIPPING_HANDLES", {});
        return;
      }

      if (response?.metadata?.action_urls?.[CheckoutActions.ADDRESS_SELECT]?.success_url) {
        redirectUrl(response.metadata.action_urls[CheckoutActions.ADDRESS_SELECT].success_url);
        return;
      }

      if (
        actionUrls &&
        actionUrls[CheckoutActions.ADDRESS_SELECT] &&
        actionUrls[CheckoutActions.ADDRESS_SELECT].success_url
      ) {
        redirectUrl(actionUrls[CheckoutActions.ADDRESS_SELECT].success_url);
        return;
      }

      if (initialCheckoutStep === "PAYMENTS") {
        closePopup();

        setCheckoutValidations({
          ...checkoutValidations,
          address: {
            isValid: true,
          },
        });
        mutatePayment();
        mutate(`/checkout/${checkoutId}/rewards`);
        if (Boolean(checkoutMetadata?.uiAttributes?.upsell?.isEnabled)) {
          mutate(getUpsellUrl({ checkoutId }));
        }
      }

      closePopup();
      setCheckoutView("PAYMENTS");
    } catch (e: unknown) {
      const { errorObj, errorMsg } = extractApiError(e);
      if (handleAddressFieldError(errorObj)) {
        return;
      }
      errorToast(errorMsg || t("add_address_failed"));
      setCheckoutView("ADDRESS_LIST");
      setIsLoading(false);
      if (
        actionUrls &&
        actionUrls[CheckoutActions.ADDRESS_SELECT] &&
        actionUrls[CheckoutActions.ADDRESS_SELECT].failure_url
      ) {
        redirectUrl(actionUrls[CheckoutActions.ADDRESS_SELECT].failure_url);
      }
    }
  };

  const onTwoStepFormSubmit = async () => {
    let authToken;
    let expiry;
    let checkoutResponse: any;
    try {
      setIsLoading(true);
      let verificationDetails: OTPVerificationDetails = {
        identifier: addDefaultCountryCode(values?.phone),
        merchantId: merchant?.merchantId ?? "",
        skipVerification: true,
        domain: inIframe() ? originUrl : constants?.CHECKOUT_HOST,
      };
      const result = await sendOtpWithPhoneNumber(verificationDetails, { context: "AUTH" });
      authToken = result.token?.access_token;
      expiry = result?.access_token_expires_at;
      setTwoStepAuthToken(authToken, expiry);
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_GUEST_LOGIN,
        eventFor: [analyticsTypes.SF_ANALYTICS],
        eventType: "flo_action",
        metaData: {
          merchantId: merchant?.merchantId,
          checkoutToken: checkoutToken,
          checkoutId: checkoutId,
          shopifySessionId: shopifySessionId,
          userData: {
            type: analyticsEvents.FLO_GUEST_LOGIN,
          },
        },
      });
      if (Boolean(user?.uid)) {
        const tokenId = searchParams.get("tokenId");
        const response = await postRequest(`/checkout/v2/checkout-tokens/${tokenId}/checkout`, {});
        checkoutResponse = await putRequest(`/checkout/v2/checkout`, { uid: response?.checkout?.uid });
      } else {
        checkoutResponse = await putRequest(`/checkout/v2/checkout`, { uid: checkoutId });
      }

      const filteredData = filterDisabledAddressFields(
        {
          ...values,
          country: values?.country || "India",
          country_code: values?.country_code || "IN",
          address1: values.address1?.trim(),
          address2: values.address2?.trim(),
          name: values.name.trim(),
          city: values.city.trim(),
          email: values.email?.trim(),
        },
        fieldsConfig,
      );
      const payload = {
        account_id: checkoutResponse?.checkout?.customer_id,
        address_type: values.type?.trim() || "OTHERS",
        data: filteredData,
      };
      const response = await postRequest(`/checkout/${checkoutId}/address`, payload, "KRATOS_PRIVATE");
      const accountResponse = await getRequest("/gatekeeper/accounts?include=address", "KRATOS_PRIVATE");
      handleResponseSuccess(response, accountResponse);
      setIsLoading(false);
      //Phone entered event
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_AUTH_PHONE_ENTERED,
        eventType: "click",
        metaData: {
          userType: "guest",
          phoneNumber: verificationDetails.identifier,
        },
      });

      const showShippingHandles = response?.metadata?.show_shipping_handle_selector ?? false;
      const shippingHandles = response?.metadata?.available_shipping_handles ?? [];
      setShippingHandles(shippingHandles as ShippingHandlesType);
      if (shippingHandles.length > 0 && Boolean(showShippingHandles) && initialCheckoutStep !== "PAYMENTS") {
        openPopup("SHIPPING_HANDLES", {});
        return;
      }
      if (initialCheckoutStep === "PAYMENTS") {
        setCheckoutModal("NONE");
      }
      setCheckoutView("PAYMENTS");
    } catch (e: unknown) {
      const { errorObj, errorMsg } = extractApiError(e);
      if (handleAddressFieldError(errorObj)) {
        return;
      }
      errorToast(errorMsg || t("add_address_failed"));
      setIsLoading(false);
    }
  };

  const handleResponseSuccess = (checkoutResponse: any, accountResponse: any) => {
    handleCheckoutResponse(checkoutResponse);
    handleAccountResponse(accountResponse, checkoutResponse);
    sendAnalyticsEvent({
      eventName: isNewAddress
        ? analyticsEvents.FLO_ADDRESS_NEW_FORM_SUBMITTED
        : analyticsEvents.FLO_ADDRESS_EDIT_FORM_SUBMITTED,
      eventType: "click",
    });

    if (initialCheckoutStep !== "PAYMENTS") {
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_ADDRESS_SELECTED,
        eventType: "click",
      });
    }
  };

  const handleCheckoutResponse = (checkoutResponse: any) => {
    updateCheckoutBasedOnCheckoutResponse(checkoutResponse);
  };

  const handleAccountResponse = (accountResponse: any, checkoutResponse: any) => {
    setUserAddress(checkoutResponse?.addresses, accountResponse?.addresses, undefined, fieldsConfig);
    setUserOrderHistory(user?.uid, merchant?.merchantId ?? "", checkoutId);
  };

  const handleInternationalAddresses = async () => {
    try {
      setIsLoading(true);
      const countriesResp = await getRequest("/v1/countries", "CHECKOUT_PUBLIC");
      if (!Boolean(countriesResp?.length)) {
        return;
      }
      setCountries(countriesResp);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCountrySelection = async (countryCode: string, hasErrors?: boolean) => {
    if (!Boolean(countryCode)) return;
    try {
      setIsLoading(true);
      const response = await Promise.allSettled([
        getProvincesByCountryCode(countryCode),
        getAddressConfigByCountryCode(countryCode, merchant?.merchantId ?? ""),
      ]);

      if (!response.length) return;

      if (response[1]?.status === "fulfilled") {
        const addressConfigResponse = response[1].value?.config?.fields;
        setFieldsConfig(addressConfigResponse);
      }

      if (response[0]?.status === "fulfilled") {
        const provincesResp = response[0].value;
        setValues((oldValues: any) => ({
          ...oldValues,
          zip: "",
          address1: "",
          address2: "",
          city: "",
          state: "",
          state_code: "",
        }));
        setErrors((oldValues) => ({
          ...oldValues,
          ...(hasErrors
            ? initialFormErrors
            : {
                zip: { ...oldValues.zip, status: false },
                address1: { ...oldValues.address1, status: false },
                address2: { ...oldValues.address2, status: false },
                city: { ...oldValues.city, status: false },
                state: { ...oldValues.state, status: false },
                country: { ...oldValues.country, status: false },
                state_code: { ...oldValues.state_code, status: false },
              }),
        }));
        setStateDisabled(false);
        if (!Boolean(provincesResp?.length)) {
          setStates([]);
          setValues((oldValues: any) => ({
            ...oldValues,
            state_code: " ",
          }));
          setIsLoading(false);
          return;
        }
        provincesResp.unshift({ code: "", name: "" });
        setStates(provincesResp);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAddressSuggestions = useDebounceCallback(async (searchValue: string) => {
    if (!searchValue || searchValue.length < 3 || !values.zip) {
      setAddressAutocompleteOptions([]);
      return;
    }

    setIsLoadingAddressSuggestions(true);
    try {
      const response = await postRequest(
        `/checkout/${checkoutId}/address/suggest`,
        {
          address: searchValue,
          pincode: values.zip,
        },
        "KRATOS_PRIVATE",
      );

      const suggestions: AutocompleteOption[] =
        response?.predictions?.map((item: any) => ({
          value: item?.address,
          label: item?.address,
        })) || [];

      setAddressAutocompleteOptions(suggestions);
    } catch (error) {
      console.error("Error fetching address suggestions:", error);
      setAddressAutocompleteOptions([]);
    } finally {
      setIsLoadingAddressSuggestions(false);
    }
  }, 500);

  const renderAddressForm = () => {
    const address1InputProps = inputProps(FormField.address1);
    const address2InputProps = inputProps(FormField.address2);

    const renderFieldError = (field: FormField) => {
      let err = errors[field];
      if (!err?.status || !err.message) return null;
      return (
        <div className="mt-1 px-0.5 flex items-center gap-1 ">
          <Icon IconComponent={Warning} size="xxxs" className="text-ouch" />
          <label
            htmlFor={field}
            className="bg-transparent text-text-xs-regular text-ouch transition-all peer-focus:hidden">
            {err.message}
          </label>
        </div>
      );
    };

    return (
      <form
        className={`flex flex-col space-y-4 pt-4`}
        id="address-form"
        noValidate
        onSubmit={isTwoStepCheckout ? handleSubmit(onTwoStepFormSubmit) : handleSubmit(onDialogFormSubmit)}>
        <fieldset className="flex flex-col space-y-4 px-4">
          {Boolean(validationSchema?.zip) && (
            <FieldWrapper field={FormField.zip}>
              <BaseInputField
                type={values.country_code === "IN" ? "tel" : "text"}
                label={t("address_pincode")}
                maxLength={values.country_code === "IN" ? 6 : 25}
                autoComplete="none"
                {...inputProps(FormField.zip, {
                  onChange: (e: any) => {
                    handleZipChange(e.target.value);
                  },
                })}
                error={Boolean(errors.zip?.status)}
                required={Boolean(validationSchema?.zip?.required)}
              />
              {renderFieldError(FormField.zip)}
            </FieldWrapper>
          )}

          {Boolean(validationSchema?.address1) && (
            <FieldWrapper field={FormField.address1}>
              <BaseTextArea
                label={validationSchema?.address1?.label}
                {...address1InputProps}
                onChange={(e) => address1InputProps.onChange?.(e)}
                onBlur={(e) => address1InputProps.onBlur?.(e as any)}
                onFocus={(e) => address1InputProps.onFocus?.(e)}
                autoComplete={
                  Boolean(validationSchema?.address1) ? "address-line1" : "shipping street-address"
                }
                error={Boolean(errors.address1?.status)}
                required={Boolean(validationSchema?.address1?.required)}
              />
              {renderFieldError(FormField.address1)}
            </FieldWrapper>
          )}
          {Boolean(validationSchema?.address2) && (
            <FieldWrapper field={FormField.address2} className="relative">
              <Autocomplete
                options={addressAutocompleteOptions}
                value={address2InputProps.value as string}
                onChange={(newValue) => {
                  if (merchant?.addressAutocompleteEnabled) {
                    fetchAddressSuggestions(newValue);
                  }
                  const syntheticEvent = {
                    target: { value: newValue },
                  } as React.ChangeEvent<HTMLTextAreaElement>;
                  address2InputProps.onChange?.(syntheticEvent);
                }}
                renderInput={(params) => (
                  <BaseTextArea
                    {...params}
                    placeholder={merchant?.addressAutocompleteEnabled ? t("start_typing_suggestions") : ""}
                    label={validationSchema?.address2?.label}
                    autoComplete={
                      Boolean(validationSchema?.address2) ? "address-line2" : "shipping street-address"
                    }
                    error={Boolean(errors.address2?.status)}
                    required={Boolean(validationSchema?.address2?.required)}
                    onBlur={(e) => {
                      params.onBlur(e);
                      address2InputProps.onBlur?.(e as any);
                    }}
                    onFocus={(e) => {
                      params.onFocus(e);
                      address2InputProps.onFocus?.(e);
                    }}
                  />
                )}
                renderOption={(option) => (
                  <div
                    onClick={() => {
                      const syntheticEvent = {
                        target: { value: option.label },
                      } as React.ChangeEvent<HTMLTextAreaElement>;
                      address2InputProps.onChange?.(syntheticEvent);
                    }}
                    className="gap-2 flex items-center py-1.5 px-3 hover:bg-gray-50 cursor-pointer rounded-md transition-colors">
                    <Icon IconComponent={MapPinArea} size="sm" />
                    <div className="text-text-sm-medium">{option.label}</div>
                  </div>
                )}
                emptyMessage={isLoadingAddressSuggestions ? "Loading..." : "No addresses found"}
              />
              {isLoadingAddressSuggestions && (
                <div className="absolute right-2.5 top-0 text-text-xxs-medium text-gray-medium pointer-events-none flex items-center space-x-1 text-primary-dark">
                  <span> Finding</span>
                  <Icon IconComponent={Spinner} size="xs" className="animate-spin text-primary-dark" />
                </div>
              )}
              {renderFieldError(FormField.address2)}
            </FieldWrapper>
          )}

          {!isLocationFieldsExpanded && !errors.zipValidation?.status && !hasLocationErrors ? (
            <LocationFieldsWidget
              onClick={() => setIsLocationFieldsExpanded(true)}
              city={values.city}
              state={values.state}
              country={values.country}
            />
          ) : (
            <>
              {Boolean(validationSchema?.city) && (
                <FieldWrapper field={FormField.city}>
                  <BaseInputField
                    type="text"
                    label={t("address_city")}
                    {...inputProps(FormField.city)}
                    autoComplete="shipping address-level2"
                    error={Boolean(errors.city?.status)}
                    required={Boolean(validationSchema?.city?.required)}
                  />
                  {renderFieldError(FormField.city)}
                </FieldWrapper>
              )}

              <div className="flex w-full flex-row items-start space-x-2">
                {Boolean(validationSchema?.state) &&
                  (states?.length ? (
                    <DropDown
                      label={t("address_state")}
                      {...inputProps(FormField.state_code, {
                        onChange: (e: any) => {
                          setValueOf("state_code", e?.target?.value);
                          setValueOf("state", e?.target?.selectedOptions[0]?.text);
                          Boolean(errors.state) && delete errors.state;
                        },
                      })}
                      autoComplete="shipping address-level1"
                      disabled={stateDisabled}
                      options={states}
                      optionKey="code"
                      optionValue="name"
                      required={Boolean(validationSchema?.state?.required)}
                      error={errors.state}
                    />
                  ) : (
                    <FieldWrapper field={FormField.state}>
                      <BaseInputField
                        type="text"
                        label={t("address_state")}
                        {...inputProps(FormField.state)}
                        autoComplete="shipping address-level1"
                        error={Boolean(errors.state?.status)}
                        required={Boolean(validationSchema?.state?.required)}
                      />
                      {renderFieldError(FormField.state)}
                    </FieldWrapper>
                  ))}

                {merchant?.addressConfig?.isInternationalAddressEnabled && (
                  <DropDown
                    label={t("country_region")}
                    {...inputProps(FormField.country_code, {
                      onChange: (e: any) => {
                        setValueOf("country_code", e?.target?.value);
                        setValueOf("country", e?.target?.selectedOptions[0]?.text);
                        handleCountrySelection(e?.target?.value);
                      },
                    })}
                    autoComplete="shipping country"
                    options={countries}
                    optionKey="code"
                    optionValue="name"
                    required={Boolean(validationSchema?.country?.required)}
                    error={errors.country_code}
                  />
                )}
              </div>
            </>
          )}
        </fieldset>
        <div className="w-full h-1 bg-zinc-950/5"></div>
        <fieldset className="px-4 mt-2">
          <div className="flex flex-col space-y-4">
            {Boolean(validationSchema?.phone) && (
              <FieldWrapper field={FormField.phone} className="relative flex flex-col">
                <PhoneInput
                  international={true}
                  countryCallingCodeEditable={false}
                  defaultCountry="IN"
                  country={values?.country_code}
                  countries={merchant?.addressConfig?.enabledCountries}
                  className={classNames(
                    "flex w-full h-[47px] !rounded-none bg-white text-zinc-800 outline-none",
                    "border-0 border-b-1 transition-all duration-200 pt-2 pb-1 px-2.5",
                    errors.phone?.status
                      ? "border-red-dark shadow-[0_2px_0px_0px_color-mix(in_srgb,var(--flo-red-dark-color)_20%,transparent)]"
                      : "border-black-10 hover:border-black-20",
                    phoneInputFocused
                      ? "border-primary-dark border-b-1 shadow-[0_2px_0px_0px_color-mix(in_srgb,var(--flo-primary-dark-color)_20%,transparent)]"
                      : "",
                  )}
                  placeholder={phoneInputFocused || values.phone ? t("phone_number") : ""}
                  {...inputProps(FormField.phone, {
                    onFocus: () => setPhoneInputFocused(true),
                    onBlur: () => setPhoneInputFocused(false),
                    onChange: (value: PhoneInputValueType) => {
                      handleFieldChange(FormField.phone, value ?? "");
                    },
                  })}
                  numberInputProps={{
                    maxLength: getPhoneInputMaxLength(values?.phone, values?.country_code as Country),
                  }}
                  error={errors.phone?.status}
                  limitMaxLength
                />
                <label
                  htmlFor={FormField.phone}
                  className={classNames(
                    "absolute left-2.5 top-[-1px] text-xxs font-medium transition-all duration-200 pointer-events-none",
                    phoneInputFocused
                      ? errors.phone?.status
                        ? "text-red-dark"
                        : "text-primary-dark"
                      : "text-gray-medium",
                  )}>
                  {t("phone_number")}
                  {validationSchema?.phone?.required && " *"}
                </label>
                {errors.phone?.status && errors.phone.message && (
                  <div className="mt-1 flex items-center gap-0.5 ">
                    <WarningCircle className="-mt-[1px] h-3 w-3 cursor-pointer text-ouch" />
                    <label
                      htmlFor={"phone"}
                      className="bg-transparent text-xs font-normal text-ouch transition-all peer-focus:hidden">
                      {errors.phone?.message}
                    </label>
                  </div>
                )}
              </FieldWrapper>
            )}

            {Boolean(validationSchema?.name) && (
              <FieldWrapper field={FormField.name}>
                <BaseInputField
                  type="text"
                  label={t("full_name")}
                  autoComplete="name"
                  {...inputProps(FormField.name)}
                  error={Boolean(errors.name?.status)}
                  required={Boolean(validationSchema?.name?.required)}
                />
                {renderFieldError(FormField.name)}
              </FieldWrapper>
            )}

            {Boolean(validationSchema?.email) && (
              <FieldWrapper field={FormField.email}>
                <BaseInputField
                  type="text"
                  label={t("email")}
                  {...inputProps(FormField.email)}
                  autoComplete="email"
                  error={Boolean(errors.email?.status)}
                  required={Boolean(validationSchema?.email?.required)}
                />
                {renderFieldError(FormField.email)}
              </FieldWrapper>
            )}
          </div>
        </fieldset>

        {!Boolean(isTwoStepCheckout) && (
          <fieldset className="mx-4">
            <>
              <span className={`text-xxs text-gray-dark`}>{t("save_as")}</span>
              <ul className="flex flex-row gap-2 flex-wrap !mt-2">
                {addressTypes.map((type: AddressTypeProps) => (
                  <li
                    onClick={() => {
                      if (type?.value === "OTHER") {
                        setShowOthersInput(true);
                        if (
                          Object.values(ADDRESS_TYPES).includes(
                            values?.type?.toUpperCase() as AddressTypeValue,
                          )
                        ) {
                          setValueOf("type", "");
                        }
                        return;
                      } else {
                        setShowOthersInput(false);
                      }
                      setValueOf("type", type?.value);
                    }}
                    key={type.id}>
                    <AddressType {...type} selectedValue={values?.type as AddressTypeValue} />
                  </li>
                ))}
              </ul>
              {showOthersInput && (
                <div className="relative w-full">
                  <input
                    type="text"
                    placeholder="E.g. Simpson's Home"
                    autoFocus
                    maxLength={48}
                    {...inputProps(FormField.type)}
                    className={classNames(
                      "flex w-full h-[47px] !rounded-none bg-white text-zinc-800 outline-none text-text-sm-regular",
                      "border-0 border-b-1 transition-all duration-200 pt-2 pb-1 px-2.5",
                      "focus:border-primary-dark focus:border-b-1 focus:shadow-[0_2px_0px_0px_color-mix(in_srgb,var(--flo-primary-dark-color)_20%,transparent)]",
                      errors?.type?.status
                        ? "border-red-dark shadow-[0_2px_0px_0px_color-mix(in_srgb,var(--flo-red-dark-color)_20%,transparent)]"
                        : "border-black-10 hover:border-black-20",
                    )}
                  />
                  {renderFieldError(FormField.type)}
                </div>
              )}
            </>
          </fieldset>
        )}

        <div className="flex flex-row gap-1 mt-2 mx-4">
          <span>
            <ShieldCheck className="h-4 w-4 text-zinc-400 text-text-xxs-medium" weight="fill" />
          </span>
          <span className="text-text-xxs-medium text-zinc-400">
            This address will be secured with OTP on Shopflo checkouts. View{" "}
            <a
              target="_blank"
              href="https://www.shopflo.com/terms-conditions"
              className="underline underline-offset-2 text-zinc-400">
              Terms and conditions
            </a>{" "}
            and{" "}
            <a
              target="_blank"
              href="https://www.shopflo.com/privacy-policy"
              className="underline underline-offset-2 text-zinc-400">
              Privacy Policy
            </a>
          </span>
        </div>

        {Boolean(isTwoStepCheckout) ? (
          <DialogFooter>
            <PrimaryButton type="submit" buttonText={t("proceed_to_pay")} height="h-14" />
          </DialogFooter>
        ) : (
          <div className="!mx-3">
            <PrimaryButton
              type="submit"
              id="flo_address_submit"
              buttonText={t("save_and_continue")}
              height="h-14"
            />
          </div>
        )}
      </form>
    );
  };

  return (
    <React.Fragment>
      {isTwoStepCheckout ? (
        <>{renderAddressForm()}</>
      ) : (
        <>
          <DialogHeader className={`${isAddressBannerEnabled() ? "!bg-white" : ""}`}>
            <div className={`flex h-full w-full flex-row items-center justify-between`}>
              <h1 className="text-base font-medium text-coal-light">
                {isNewAddress ? t("add_new_address_header") : t("edit_address")}
              </h1>
              <button className="outline-none">
                <X
                  className="h-6 w-6 cursor-pointer text-coal-light"
                  onClick={() => {
                    sendAnalyticsEvent({
                      eventName: analyticsEvents.FLO_BACK_CLICK,
                      eventType: "click",
                    });
                    closePopup();
                    if (initialCheckoutStep !== "PAYMENTS") setCheckoutView("ADDRESS_LIST");
                  }}
                />
              </button>
            </div>
          </DialogHeader>
          <DialogBody className="!pt-12">
            <GenericBannerStrip bannerType="ORDER_SUMMARY" />
            <div>{renderAddressForm()}</div>
          </DialogBody>
        </>
      )}

      {isLoading && <OverlaySpinner />}
    </React.Fragment>
  );
};

interface LocationFieldsWidgetProps {
  onClick: () => void;
  city?: string;
  state?: string;
  country?: string;
}

const LocationFieldsWidget: React.FC<LocationFieldsWidgetProps> = ({ onClick, city, state, country }) => {
  const hasLocationValues = Boolean(city) || Boolean(state);
  const displayText = hasLocationValues
    ? `${city || ""}${city && state ? ", " : ""}${state || ""}`
    : "Choose State, City";

  return (
    <div
      onClick={onClick}
      className="flex cursor-pointer flex-row items-center justify-between rounded-lg px-3 py-2 transition-colors"
      style={{ backgroundColor: "rgba(113, 113, 123, 0.04)" }}>
      <div className="flex cursor-pointer flex-row items-center gap-2">
        <div className="flex h-fit w-fit items-center justify-center">
          {hasLocationValues ? (
            <Icon
              IconComponent={CheckCircle}
              iconProps={{ weight: "bold" }}
              size="xs"
              className="text-emerald-500"
            />
          ) : (
            <Icon IconComponent={Globe} size="xs" className="text-zinc-400" />
          )}
        </div>
        <div className="flex flex-1 flex-row items-center gap-1 pt-[1px]">
          <span
            className={classNames(
              "relative text-text-xs-medium",
              hasLocationValues ? "text-zinc-500" : "text-zinc-400",
            )}>
            {displayText}
          </span>
          <span className="w-[1px] h-[10px] bg-zinc-400 mx-1"></span>
          <span className="text-text-xs-medium text-zinc-500">{country || "India"}</span>
        </div>
      </div>
      <div>
        <Icon IconComponent={PencilSimpleLine} size="xs" className="text-zinc-400" />
      </div>
    </div>
  );
};

const AddressType: React.FC<AddressTypeProps & { selectedValue?: AddressTypeValue }> = ({
  value,
  id,
  label,
  isDisabled,
  icon,
  selectedValue,
}) => {
  const isSelected = selectedValue?.toUpperCase() === value?.toUpperCase();

  const baseClasses =
    "flex h-7 px-2.5 space-x-1 min-w-max max-w-full items-center justify-center rounded-2xl bg-gray-lightest text-base font-normal text-coal-dark";
  const disabledClasses = isDisabled ? "cursor-not-allowed opacity-40" : "cursor-pointer";
  const selectedClasses = isSelected
    ? "border-transparent font-medium ring-1 ring-primary-dark shadow-[0_0_4px_2px] shadow-primary-light"
    : "ring-1 ring-transparent font-mediu";
  const hoverClasses = !isSelected && !isDisabled ? "hover:bg-gray-lighter" : "";

  return (
    <div className="relative">
      <div id={id} className={classNames(baseClasses, disabledClasses, selectedClasses, hoverClasses)}>
        <Icon
          IconComponent={icon}
          size="xs"
          iconProps={{
            weight: isSelected ? "fill" : "duotone",
          }}
        />
        <span className="text-text-xs-medium pt-0.5">{label}</span>
      </div>
    </div>
  );
};

export default AddressForm;
